import {skipToken} from '@reduxjs/toolkit/query';
import {IconButton, openDeleteDialog} from 'platform/components';
import {Box, HStack} from 'platform/foundation';

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useSelectedFilters} from '../../../../hooks/SelectedFiltersContext';
import {usePriceReport} from '../../../../hooks/usePriceReport';
import {useTrimLevelsCatalog} from '../../../../hooks/useTrimLevelsCatalog';
import i18n from '../../../../i18n/i18n';
import {
  useCreateRelativeFilterMutation,
  useDeleteRelativeFilterMutation,
  useGetRelativeFiltersQuery,
  useUpdateRelativeFilterMutation,
} from '../../../../store/omneticApi';
import {RelativeFilterConfigBody, RelativeFilterResponseBody} from '../../../../types/api/api';
import {FilterFormData} from '../../../../types/FilterFormData';
import {PriceReportType} from '../../../../types/PriceReportType';
import {handleApiError} from '../../../../utils/handleApiError';
import {PresetEditorDialog} from './components/PresetEditorDialog';
import {SavedFiltersDropdown} from './components/SavedFiltersDropdown';
import {applyPresetToForm} from './utils/applyPresetToForm';
import {isPresetControlledFormField} from './utils/presetControlledFormFields';

interface SavedFiltersProps extends TestIdProps {
  priceReport: PriceReportType;
  formApi: UseFormReturn<FilterFormData>;
  onFilterChange: () => void;
}

type DialogState = {mode: 'closed'} | {mode: 'create'} | {mode: 'edit'; presetId: string};

export function SavedFilters(props: SavedFiltersProps) {
  const {priceReport, formApi, onFilterChange} = props;
  const {requestData} = usePriceReport();
  /* customerId is deleted from price-report when the report is regenerated, thus using customerId from requestData */
  const customerId = requestData?.customerId;

  const firstRegistrationOnYearGte = useWatch({
    control: formApi.control,
    name: 'firstRegistrationOnYearGte',
  });
  const firstRegistrationOnYearLte = useWatch({
    control: formApi.control,
    name: 'firstRegistrationOnYearLte',
  });
  const {catalog: trimLevelsCatalog} = useTrimLevelsCatalog(
    priceReport,
    firstRegistrationOnYearGte,
    firstRegistrationOnYearLte
  );

  const {
    data: presets = [],
    isLoading,
    isError,
  } = useGetRelativeFiltersQuery(isNotNil(customerId) ? {customerId} : skipToken);
  const [createRelativeFilter] = useCreateRelativeFilterMutation();
  const [updateRelativeFilter] = useUpdateRelativeFilterMutation();
  const [deleteRelativeFilter] = useDeleteRelativeFilterMutation();

  const [activePresetId, setActivePresetId] = useState<string | null>(null);
  const [dialogState, setDialogState] = useState<DialogState>({mode: 'closed'});

  // Tracks programmatic form updates from preset application so the watch
  // subscription below doesn't clear the active preset during those updates.
  const isApplyingPresetRef = useRef(false);

  const applyPreset = useCallback(
    (config: RelativeFilterConfigBody) => {
      isApplyingPresetRef.current = true;
      try {
        applyPresetToForm(config, priceReport, formApi, onFilterChange, trimLevelsCatalog);
      } finally {
        isApplyingPresetRef.current = false;
      }
    },
    [priceReport, formApi, onFilterChange, trimLevelsCatalog]
  );

  // Manual edits to preset-controlled fields (or "Reset filters" via formApi.reset)
  // clear the active preset so the dropdown stops showing a stale selection.
  // Toggles and location address are not part of a preset and must not clear it.
  useEffect(
    function resetActivePresetOnFormChange() {
      const subscription = formApi.watch((_, {name}) => {
        if (isApplyingPresetRef.current) {
          return;
        }
        // name === undefined means formApi.reset() (full reset) — clear the selection.
        // Only skip for a named field explicitly outside the preset's scope.
        if (isNotNil(name) && !isPresetControlledFormField(name)) {
          return;
        }
        setActivePresetId((current) => (isNil(current) ? current : null));
      });
      return () => subscription.unsubscribe();
    },
    [formApi, setActivePresetId]
  );

  const {hasAnyFilter} = useSelectedFilters();
  const systemPreset = useMemo(() => presets.find((p) => p.system) ?? null, [presets]);

  // When no filters are applied (URL has none → form is at per-vehicle defaults),
  // show the system "Exact match" preset as selected without mutating the form
  // or the URL. User's explicit selection always takes precedence.
  const displayedPresetId =
    activePresetId ?? (!hasAnyFilter && isNotNil(systemPreset) ? systemPreset.id : null);

  const handlePresetChange = useCallback(
    (presetId: string | null) => {
      if (isNil(presetId)) {
        return;
      }
      const found = presets.find((p) => p.id === presetId);
      if (isNil(found)) {
        return;
      }
      setActivePresetId(presetId);
      applyPreset(found.config);
    },
    [presets, applyPreset, setActivePresetId]
  );

  const handleCreateOption = useCallback(() => {
    setDialogState({mode: 'create'});
  }, []);

  const handleSave = useCallback(
    (name: string, config: RelativeFilterConfigBody) => {
      if (isNil(customerId)) {
        return;
      }

      if (dialogState.mode === 'create') {
        createRelativeFilter({customerId, name, config})
          .unwrap()
          .then(({id}) => {
            setActivePresetId(id);
            applyPreset(config);
            setDialogState({mode: 'closed'});
          })
          .catch(handleApiError);
      } else if (dialogState.mode === 'edit') {
        const editedPresetId = dialogState.presetId;
        updateRelativeFilter({customerId, id: editedPresetId, name, config})
          .unwrap()
          .then(() => {
            if (activePresetId === editedPresetId) {
              applyPreset(config);
            }
            setDialogState({mode: 'closed'});
          })
          .catch(handleApiError);
      }
    },
    [
      dialogState,
      activePresetId,
      createRelativeFilter,
      updateRelativeFilter,
      applyPreset,
      customerId,
      setActivePresetId,
    ]
  );

  const deletePreset = useCallback(
    (presetId: string) => {
      if (isNil(customerId)) {
        return Promise.resolve();
      }
      return deleteRelativeFilter({id: presetId, customerId})
        .unwrap()
        .then(() => {
          setActivePresetId((current) => (current === presetId ? null : current));
          setDialogState({mode: 'closed'});
        })
        .catch(handleApiError);
    },
    [customerId, deleteRelativeFilter]
  );

  const handleDeletePresetPress = useCallback(() => {
    if (dialogState.mode !== 'edit' || isNil(customerId)) {
      return;
    }
    const presetId = dialogState.presetId;
    const presetName = presets.find((p) => p.id === presetId)?.name ?? '';
    openDeleteDialog({
      id: 'price-report-delete-saved-filter-preset',
      text: i18n.t('entity.filterForm.savedFilters.dialog.deleteConfirm', {name: presetName}),
      onConfirm: () => deletePreset(presetId),
      'data-testid': suffixTestId('deletePresetDeleteDialog', props),
    });
  }, [dialogState, customerId, presets, deletePreset, props]);

  const dialogPreset = useMemo((): RelativeFilterResponseBody | null => {
    if (dialogState.mode === 'create') {
      return null;
    }
    if (dialogState.mode === 'edit') {
      return presets.find((p) => p.id === dialogState.presetId) ?? null;
    }
    return null;
  }, [dialogState, presets]);

  const isDialogOpen = dialogState.mode !== 'closed';

  return (
    <>
      <HStack align="flex-end" spacing={1}>
        <Box flexGrow={1} minWidth={0}>
          <SavedFiltersDropdown
            label={i18n.t('entity.filterForm.savedFilters.label')}
            presets={presets}
            selectedPresetId={displayedPresetId}
            placeholder={i18n.t('entity.filterForm.savedFilters.placeholder')}
            customerId={customerId}
            isLoading={isLoading}
            isError={isError}
            onSelectPreset={handlePresetChange}
            onCreatePreset={handleCreateOption}
            data-testid={suffixTestId('savedFiltersDropdown', props)}
          />
        </Box>
        <IconButton
          icon="action/settings"
          priority="tertiary"
          isDisabled={isNil(displayedPresetId)}
          onClick={() => {
            if (isNotNil(displayedPresetId)) {
              setDialogState({mode: 'edit', presetId: displayedPresetId});
            }
          }}
          aria-label={i18n.t('entity.filterForm.savedFilters.editPreset')}
          data-testid={suffixTestId('editPresetButton', props)}
        />
      </HStack>

      <PresetEditorDialog
        key={dialogState.mode === 'edit' ? dialogState.presetId : dialogState.mode}
        isOpen={isDialogOpen}
        initialPreset={dialogPreset}
        onSave={handleSave}
        onDelete={dialogState.mode === 'edit' ? handleDeletePresetPress : undefined}
        onClose={() => setDialogState({mode: 'closed'})}
        data-testid={suffixTestId('presetEditor', props)}
      />
    </>
  );
}
